import React, {Component} from "react";
import {Dialog, IconButton, Tooltip, withStyles} from "@material-ui/core";
import {ArrowBack, Edit, People} from "@material-ui/icons";
import {request} from "../../../api-client";
import {getUpdateFormImageUrlFromItem, setStatePromise} from "../../../utils";
import MessagesContainer from "./MessagesContainer";
import ParticipantsButton from "./ParticipantsButton";
import localization from "../../../config/localization";
import {getStoredUser, homePathGenerator, userIsSysAdmin} from "../../../api-client/core/authentication/utils";
import ReceiveEmailsFromPitch from "./ReceiveEmailsFromPitch";
import browserHistory from "../../../utils/browserHistory";
import styled from "styled-components";
import {FormViewContainer} from "../../../components/Letflow/ViewContainer";
import {TextValidator} from "react-material-ui-form-validator";
import InputLabel from "@material-ui/core/InputLabel";
import FilePondUploader from "../../../components/Letflow/FilePondUploader";
import Popper from "@material-ui/core/Popper";
import Grow from "@material-ui/core/Grow";
import Paper from "@material-ui/core/Paper";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import MenuItem from "@material-ui/core/MenuItem";
import MoreVert from "@material-ui/icons/MoreVert";
import HomeSearchInput from "../../../components/Letflow/HomeSearchInput";

const fetchPitchLastMessages = (pitchId, { filterByMessage } = {}) =>
  request.pitch.showLastMessages(pitchId, { filterByMessage });

const fetchPitchFirstMessages = (pitchId, { filterByMessage } = {}) =>
  request.pitch.showFirstMessages(pitchId, { filterByMessage });

const fetchPitchPreviousMessages = (
  pitchId,
  messageId,
  { filterByMessage, amount } = {}
) =>
  request.pitch.showPreviousMessages(pitchId, messageId, {
    filterByMessage,
    amount
  });

const fetchPitchNextMessages = (
  pitchId,
  messageId,
  { filterByMessage, amount } = {}
) =>
  request.pitch.showNextMessages(pitchId, messageId, {
    filterByMessage,
    amount
  });

export const fetchPitch = pitchId => request.pitch.show(pitchId);

export const ThreadContext = React.createContext({
  pitch: null,
});


const ProfileMenuItem = withStyles({
  root: {
    fontSize: "14px",
    justifyContent: "flex-end",
    paddingBottom: "0px",
    paddingTop: "0px",
    paddingRight: "40px",
  },
})(MenuItem);

export default class Thread extends Component {
  constructor(props) {
    super(props);

    this.state = {
      pitch: null,
      pitchMessages: [],
      searchValue: "",
      isSearching: false,
      hasPrevious: false,
      hasNext: false,
      hideMessages: false,
      open: false,
      showDialogParticipant: false,
      openUpdateDialog: false,
      editingProject: '',
      name: '',
      description: '',
      project: null,
      disableForm: false,
      imageId: "",
      image: { id: null, src: null, file: null },
    };

    this.messageContainerRef = React.createRef();
  }

  componentDidMount = () => {
    this.fetchPitch()
      .then(this.fetchPitchLastMessages)
      .then(
        () =>
          (this.updateInterval = setInterval(
            () => this.fetchPitchNextMessages(),
            10000
          ))
      );
  };

  handleClose = event => {
    if (this.anchorEl.contains(event.target)) {
      return;
    }
    this.setState({ open: false });
  };
  componentWillUnmount = () => clearInterval(this.updateInterval);

  fetchPitch = async () => {
    const pitch = await fetchPitch(this.props.pitchId);

    if (!pitch.user_belongs_to_pitch && !userIsSysAdmin()) {
      browserHistory.goBack();
    }

    await setStatePromise(this, { pitch });
  };

  fetchPitchLastMessages = async ({ scrollToBottom = true } = {}) => {
    const { has_more, data: pitchMessages } = await fetchPitchLastMessages(
      this.state.pitch.id,
      { filterByMessage: this.state.searchValue }
    );

    await setStatePromise(this, { pitchMessages, hasPrevious: has_more });

    if (scrollToBottom) {
      this.scrollToBottomOfMessageContainer();
    }
  };

  fetchPitchFirstMessages = async () => {
    let { has_more, data: pitchMessages } = await fetchPitchFirstMessages(
      this.state.pitch.id,
      { filterByMessage: this.state.searchValue }
    );

    pitchMessages = pitchMessages.slice(0).reverse();

    await setStatePromise(this, { pitchMessages, hasNext: has_more });
  };

  fetchPitchPreviousMessages = async ({
    scroll = true,
    messageId,
    amount
  } = {}) => {
    if (!messageId) {
      const initialId = this.state.pitchMessages[0]
        ? this.state.pitchMessages[0].id
        : 0;

      const lowestMessageId = this.state.pitchMessages.reduce(
        (acc, next) => (acc < next.id ? acc : next.id),
        initialId
      );

      messageId = this.state.pitchMessages.length === 0 ? 0 : lowestMessageId;
    }

    let { has_more, data: pitchMessages } = await fetchPitchPreviousMessages(
      this.state.pitch.id,
      messageId,
      { filterByMessage: this.state.searchValue, amount }
    );

    pitchMessages = this.state.pitchMessages.concat(pitchMessages);

    await setStatePromise(this, { pitchMessages, hasPrevious: has_more });

    if (scroll) {
      this.messageContainerRef.current.scrollToMessage(messageId);
    }
  };

  fetchPitchNextMessages = async ({
    scrollToBottom,
    messageId,
    amount
  } = {}) => {
    if (!messageId) {
      const initialId = this.state.pitchMessages[0]
        ? this.state.pitchMessages[0].id
        : 0;

      const lowestMessageId = this.state.pitchMessages.reduce(
        (acc, next) => (acc > next.id ? acc : next.id),
        initialId
      );

      messageId = this.state.pitchMessages.length === 0 ? 0 : lowestMessageId;
    }

    let { has_more, data: pitchMessages } = await fetchPitchNextMessages(
      this.state.pitch.id,
      messageId,
      { filterByMessage: this.state.searchValue, amount }
    );

    pitchMessages = pitchMessages.slice(0).reverse();

    pitchMessages = pitchMessages.concat(this.state.pitchMessages);

    await setStatePromise(this, { pitchMessages, hasNext: has_more });

    if (scrollToBottom) {
      this.scrollToBottomOfMessageContainer();
    }
  };

  scrollToBottomOfMessageContainer = () =>
    this.messageContainerRef.current.scrollToBottom();

  currentUserIsPitchAdministrator = () =>
    this.state.pitch.participants.some(
      participant =>
        participant.id === getStoredUser().id &&
        participant.pivot.is_admin === 1
    );

  onSearch = (searchValue = this.state.searchValue) => {
    setStatePromise(this, {
      searchValue,
      isSearching: searchValue.length > 0,
      pitchMessages: [],
      hasNext: false,
      hasPrevious: false
    }).then(() => {
      if (searchValue.length > 0) {
        this.fetchPitchFirstMessages();
      } else {
        this.fetchPitchLastMessages({ scrollToBottom: true });
      }
    });
  };

  onMessageClick = message => {
    if (this.state.isSearching) {
      setStatePromise(this, {
        pitchMessages: [],
        isSearching: false,
        searchValue: "",
        hideMessages: true,
      })
        .then(() =>
          this.fetchPitchNextMessages({
            scrollToBottom: false,
            messageId: message.id - 1,
            amount: 20
          })
        )
        .then(() =>
          this.fetchPitchPreviousMessages({
            scroll: false,
            messageId: message.id,
            amount: 3
          })
        )
        .then(() => setStatePromise(this, { hideMessages: false }));
    }
  };


  handleToggle = () => {
    this.setState({ open: !this.state.open, openNotification: false });
  };

  makeUpdateDialog = () =>
    <Dialog open={this.state.openUpdateDialog} onBackdropClick={() => this.setState({openUpdateDialog: false})}>
      <FormViewContainer
        title={localization.get('project.edit')}
        submitRequest={() => this.submitRequest(this.state.editingProject)}
        onBack={() => this.setState({openUpdateDialog: false})}
        style={{maxWidth: "100%", width: "600px"}}
        color="black"
        smallExtraSpace={true}
      >
        <TextValidator
          style={{width: "100%"}}
          id="name"
          label={localization.get('form.name')}
          name="name"
          value={this.state.name}
          onChange={this.onChange}
          validators={['required']}
          errorMessages={[localization.get('validator.is_required')]}
        />
        <br/>
        <br/>
        <TextValidator
          style={{width: "100%"}}
          id="description"
          label={localization.get('form.description')}
          name="description"
          value={this.state.description}
          onChange={this.onChange}
          validators={['required']}
          errorMessages={[localization.get('validator.is_required')]}
        />
        <br/>
        <br/>
        <InputLabel>{localization.get('project.image')}</InputLabel>
        <FilePondUploader
          disableForm={this.state.disableForm}
          updateDisableForm={value => this.setState({disableForm: value})}
          file={this.state.image}
          id={"image"}
          type={"image"}
          allowedTypes={["image/*"]}
          onIdChange={this.onImageChange}
          setPreview={this.setPreview}
        />
      </FormViewContainer>
    </Dialog>


  submitRequest = (id) =>
    request.pitch.update({id, name: this.state.name, description: this.state.description, imageId: this.state.imageId})
      .then(() => this.setState({openUpdateDialog: false}))
      .then(()=> this.props.updatePitch());

  onEdit = project => this.setState({openUpdateDialog: true, editingProject: project.id, name: project.project_name, description: project.project_details});
  onChange = e => this.setState({[e.target.id]: e.target.value})

  onImageChange = image => image ? this.setState({imageId: image.id, image: {src: null, filename: null }, imagePreview: this.formNewUrl(image.path)}) : this.setState({imageId: null, image: { src: null, filename: null }})
  setPreview = () => {
    let item = {image: {id: this.state.imageId, src: this.state.imagePreview}}
    this.setState({image : {src: getUpdateFormImageUrlFromItem(item)}})
  }

  render = () => {
    const {
      pitch,
      pitchMessages,
      hasNext,
      hasPrevious,
      isSearching,
      hideMessages,
      open,
      showDialogParticipant,
    } = this.state;

    if (pitch === null) return null;

    const belongsToConversation = pitch.user_belongs_to_pitch;
    return (
      <ThreadContext.Provider
        value={{
          pitch: this.state.pitch,
        }}
      >
        <RespDiv>
          <div style={{display: 'flex', justifyContent: 'space-between', width: "100%"}}>
            <div style={{display: 'flex', flexDirection: 'column'}}>
              <div style={{display: 'flex', flexDirection: 'row', alignItems: 'center'}}>
                <IconButton style={{marginRight: 15}} onClick={() => browserHistory.push(homePathGenerator('projects'))}>
                  <ArrowBack />
                </IconButton>
                <Tooltip title={pitch.project_details}>
                  <h3 style={{margin: "20px 0 20px"}}>{pitch.project_name}</h3>
                </Tooltip>
              </div>
            </div>
            <div style={{display: 'flex', flexDirection: 'row', alignItems: "center"}}>
              {belongsToConversation &&
                <HomeSearchInput
                  styles={{marginTop: 5, width: "100%", height: 40}}
                  value={this.state.searchValue}
                  placeholder={"search_message"}
                  onKeyPress={e => {
                    if (e.key === "Enter") this.onSearch();
                  }}
                  onClear={() => {this.setState({searchValue: ""}, this.onSearch)}}
                  onSearch={value => this.setState({searchValue: value})}
                />
              }
              <div>
                <IconButton
                  style={{position: 'relative', float: 'left'}}
                  buttonRef={node => {
                    this.anchorEl = node;
                  }}
                  aria-owns={open ? "menu-list-grow" : null}
                  aria-haspopup="true"
                  onClick={this.handleToggle}
                >
                  <MoreVert style={{marginTop: 5}} />
                </IconButton>
                <Popper open={open} anchorEl={this.anchorEl} transition style={{  top: -5, left: -50 }}>
                  {({ TransitionProps, placement }) => (
                    <Grow
                      {...TransitionProps}
                      id="menu-list-grow"
                      className={"popper-menu-project"}
                      style={{ transformOrigin: placement === "bottom" ? "center top" : "center bottom",  }}
                    >
                      <Paper>
                        <ClickAwayListener onClickAway={this.handleClose}>
                          <ul style={{marginBlockStart:10, paddingInlineStart: 15, paddingBlockEnd: 15, paddingBlockStart: 15, width: 250}}>
                            <ProfileMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                if (!pitch.participants) {
                                  this.requestPitch()
                                    .then(() => this.setState({showDialogParticipant: true}))
                                } else {
                                  this.setState({showDialogParticipant: true})
                                }
                              }}
                              style={{color: "black"}}
                            >
                              <div style={{display: 'flex', alignItems: 'center'}}>
                                <People style={{fontSize: '16px', marginRight: '10px'}}/>
                                {localization.get("participants")}
                              </div>
                            </ProfileMenuItem>
                            <ProfileMenuItem
                              onClick={() => this.onEdit(pitch)}
                              style={{color: "black"}}
                            >
                              <div style={{display: 'flex', alignItems: 'center'}}>
                                <Edit  style={{fontSize: '16px', marginRight: '10px'}}/>
                                {localization.get('edit')}
                              </div>
                            </ProfileMenuItem>
                            {belongsToConversation && (
                              <ReceiveEmailsFromPitch
                                pitchId={pitch.id}
                              />
                            )}
                          </ul>
                        </ClickAwayListener>
                      </Paper>
                    </Grow>
                  )}
                </Popper>
              </div>
            </div>
          </div>
          <MessagesContainer
            ref={this.messageContainerRef}
            hasNext={hasNext}
            onNext={() =>
              this.fetchPitchNextMessages({ scrollToBottom: false })
            }
            hasPrevious={hasPrevious}
            fetchPitchNextMessages={this.fetchPitchNextMessages}
            onPrevious={() => this.fetchPitchPreviousMessages({ scroll: true })}
            pitch={pitch}
            pitchMessages={hideMessages ? [] : pitchMessages.slice(0).reverse()}
            pitchId={pitch.id}
            isSearching={isSearching}
            isAdministrator={this.currentUserIsPitchAdministrator()}
            onMessageClick={this.onMessageClick}
            onDelete={message => {
              this.setState({
                pitchMessages: this.state.pitchMessages.filter(
                  x => x.id !== message.id
                )
              });
            }}
          />
        </RespDiv>
        <ParticipantsButton
          handleClose={() => this.setState({showDialogParticipant: false})}
          pitchId={pitch.id}
          participants={pitch.participants}
          tooltip="bottom"
          open={showDialogParticipant}
        />
        {this.makeUpdateDialog()}
      </ThreadContext.Provider>
    );
  };
}

const RespDiv = styled.div`
  padding: 40px 50px 0;
  float: left;
  width:100%;
 @media (max-width: 1500px) {
   padding: 40px 50px 0;
}
 @media (max-width: 1150px) {
   padding: 40px 50px 0;
}
 @media (max-width: 850px) {
   padding: 40px 50px 0;
}
 @media (max-width: 650px) {
   padding: 40px 50px 0;
}
 @media (max-width: 470px) {
   padding: 40px 50px 0;
}
`;
